import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../styles/jianpu.css";
import { parseJianpu, songEvents, quantizeTake } from "../lib/jianpu";
import { listSavedSongs, getSavedSong, saveSong, deleteSong } from "../lib/jianpuStorage";
import JianpuSheet from "../components/jianpu/JianpuSheet";
import useJianpuPlayer, { startMetronome } from "../components/jianpu/useJianpuPlayer";
import useEasyPlay from "../components/jianpu/useEasyPlay";

const TEMPLATE = `title: New Song
key: 1=C
time: 4/4
tempo: 100

// type here — numbers 1-7, 0 for rests, | for bars
// 1' is up an octave, 3_ an eighth, 2. dotted, - holds a beat
1 2 3 -`;

const GRIDS = [
  { label: "♪ eighths", value: 0.5 },
  { label: "♬ sixteenths", value: 0.25 },
];

// Split-pane composer: plain-text jianpu on the left, live notation on the
// right, with inline parse errors. Record straight from the EasyPlay —
// count-in, play, and the take lands in the text quantized to the grid.
const JianpuCompose = () => {
  const [params] = useSearchParams();
  const loadId = params.get("load");

  const [text, setText] = useState(() => {
    if (loadId) {
      const s = getSavedSong(loadId);
      if (s) return s.text;
    }
    return TEMPLATE;
  });
  const [savedId, setSavedId] = useState(() => (loadId && getSavedSong(loadId) ? loadId : null));
  const [savedList, setSavedList] = useState(() => listSavedSongs());
  const [savedFlash, setSavedFlash] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const { song, errors } = useMemo(() => parseJianpu(text), [text]);
  const player = useJianpuPlayer();

  // ----- recording -----
  const [recState, setRecState] = useState("idle"); // idle | countin | recording
  const [grid, setGrid] = useState(0.5);
  const recRef = useRef({ notes: [], open: new Map(), zeroMs: 0, metronome: null });
  const recStateRef = useRef(recState);
  recStateRef.current = recState;

  const onNote = useCallback(({ midi, timeMs }) => {
    if (recStateRef.current === "idle") return;
    const rec = recRef.current;
    const t = timeMs - rec.zeroMs;
    if (t < 0) return; // still counting in
    const note = { midi, timeMs: t };
    rec.notes.push(note);
    rec.open.set(midi, note);
  }, []);

  const onNoteOff = useCallback(({ midi, timeMs }) => {
    const rec = recRef.current;
    const note = rec.open.get(midi);
    if (note) {
      note.durMs = Math.max(30, timeMs - rec.zeroMs - note.timeMs);
      rec.open.delete(midi);
    }
  }, []);

  const ep = useEasyPlay({ onNote, onNoteOff });

  const stopRecording = useCallback(
    (keep) => {
      const rec = recRef.current;
      if (rec.metronome) rec.metronome.stop();
      rec.metronome = null;
      setRecState("idle");
      if (!keep) return;
      if (rec.notes.length === 0) return;
      const take = quantizeTake(rec.notes, {
        bpm: song.tempo,
        tonicMidi: ep.tonicMidi,
        grid,
        beatsPerBar: song.beatsPerBar,
      });
      setText((prev) => `${prev.replace(/\s+$/, "")}\n\n// recorded take\n${take}\n`);
    },
    [song.tempo, song.beatsPerBar, grid, ep.tonicMidi]
  );

  const startRecording = () => {
    player.stop();
    const beatMs = 60000 / song.tempo;
    const countIn = song.beatsPerBar;
    recRef.current = { notes: [], open: new Map(), zeroMs: 0, metronome: null };
    setRecState("countin");
    const handle = startMetronome({
      bpm: song.tempo,
      beatsPerBar: song.beatsPerBar,
      onBeat: (b) => {
        if (b === countIn) setRecState("recording");
      },
    });
    recRef.current.metronome = handle;
    recRef.current.zeroMs = handle.startAtMs + countIn * beatMs;
  };

  useEffect(() => {
    const rec = recRef.current;
    return () => {
      if (rec.metronome) rec.metronome.stop();
    };
  }, []);

  // ----- save / load / files -----
  const doSave = () => {
    const entry = saveSong({ id: savedId, title: song.title || "Untitled", text });
    setSavedId(entry.id);
    setSavedList(listSavedSongs());
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  };

  const loadSaved = (id) => {
    const s = getSavedSong(id);
    if (!s) return;
    player.stop();
    stopRecording(false);
    setText(s.text);
    setSavedId(id);
  };

  const removeSaved = (id) => {
    deleteSong(id);
    if (id === savedId) setSavedId(null);
    setSavedList(listSavedSongs());
  };

  const doExport = () => {
    const name = (song.title || "song").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.jianpu.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doImport = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result));
      setSavedId(null);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const doPlay = () => {
    stopRecording(false);
    player.start({
      events: songEvents(song),
      bpm: song.tempo,
      beatsPerBar: song.beatsPerBar,
      totalBeats: song.totalBeats,
      metronome: false,
      guide: true,
    });
  };

  const hardErrors = errors.filter((e) => !e.warning);
  const warnings = errors.filter((e) => e.warning);

  return (
    <div className="jp-root">
      <div className="jp-shell">
        <div className="jp-btn-row jp-no-print" style={{ justifyContent: "space-between" }}>
          <Link to="/jianpu" className="jp-back">
            ← jianpu studio
          </Link>
          <span className="jp-kicker">composer</span>
        </div>

        <div className="jp-btn-row jp-no-print" style={{ marginTop: "1.1rem" }}>
          {!player.playing ? (
            <button
              type="button"
              className="jp-btn jp-btn--primary"
              onClick={doPlay}
              disabled={recState !== "idle" || song.notes.length === 0}
            >
              ▶ play
            </button>
          ) : (
            <button type="button" className="jp-btn" onClick={player.stop}>
              ■ stop
            </button>
          )}

          {recState === "idle" ? (
            <button
              type="button"
              className="jp-btn"
              onClick={startRecording}
              disabled={!ep.connected || player.playing}
              title={ep.connected ? "" : "plug the EasyPlay in first"}
            >
              ● record from instrument
            </button>
          ) : (
            <button type="button" className="jp-btn jp-btn--active" onClick={() => stopRecording(true)}>
              <span className="jp-rec-dot" />{" "}
              {recState === "countin" ? "count-in…" : "recording — tap to finish"}
            </button>
          )}

          <span className="jp-seg">
            {GRIDS.map((g) => (
              <button
                key={g.value}
                type="button"
                className={grid === g.value ? "jp-seg--on" : ""}
                onClick={() => setGrid(g.value)}
              >
                {g.label}
              </button>
            ))}
          </span>

          <button type="button" className="jp-btn" onClick={doSave}>
            {savedFlash ? "saved ✓" : savedId ? "save" : "save to this device"}
          </button>
          <button type="button" className="jp-btn" onClick={doExport}>
            export .txt
          </button>
          <label className="jp-btn" style={{ cursor: "pointer" }}>
            import
            <input type="file" accept=".txt,.jianpu" onChange={doImport} style={{ display: "none" }} />
          </label>
          <button type="button" className="jp-btn" onClick={() => window.print()}>
            print tabs
          </button>
          <button type="button" className="jp-btn" onClick={() => setShowHelp((v) => !v)}>
            {showHelp ? "hide" : "format"} guide
          </button>
        </div>

        {recState !== "idle" && (
          <div className="jp-banner jp-no-print" style={{ marginTop: "0.8rem" }}>
            {recState === "countin"
              ? `count-in: one bar of ${song.time.num}/${song.time.den} at ♩=${song.tempo}…`
              : `recording at ♩=${song.tempo}, quantizing to ${grid === 0.5 ? "eighths" : "sixteenths"} — play, then tap finish`}
          </div>
        )}

        {showHelp && (
          <div className="jp-panel jp-help jp-no-print" style={{ marginTop: "1rem" }}>
            <strong>The format</strong> — header lines (<code>title:</code>,{" "}
            <code>key: 1=C</code>, <code>time: 4/4</code>, <code>tempo: 100</code>), then
            music. <code>{"//"}</code> starts a comment.
            <table>
              <tbody>
                <tr>
                  <td><code>1</code>–<code>7</code></td>
                  <td>scale degrees (movable do); <code>0</code> is a rest</td>
                </tr>
                <tr>
                  <td><code>1'</code> <code>1''</code> <code>7,</code></td>
                  <td>octave up / two up / down — dots above or below the number</td>
                </tr>
                <tr>
                  <td><code>3_</code> <code>3=</code></td>
                  <td>eighth (one underline), sixteenth (two)</td>
                </tr>
                <tr>
                  <td><code>2.</code> <code>3_.</code></td>
                  <td>dotted — one and a half times the length</td>
                </tr>
                <tr>
                  <td><code>-</code></td>
                  <td>hold the previous note one more beat: <code>1 - -</code></td>
                </tr>
                <tr>
                  <td><code>|</code></td>
                  <td>bar line</td>
                </tr>
                <tr>
                  <td><code>#4</code> <code>b7</code></td>
                  <td>sharps and flats — the small gray buttons</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="jp-compose-grid">
          <div className="jp-no-print">
            <textarea
              className="jp-editor"
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              aria-label="jianpu text editor"
            />
            {(hardErrors.length > 0 || warnings.length > 0) && (
              <div className="jp-errors">
                {hardErrors.map((e, i) => (
                  <span key={`e${i}`} className="jp-error">
                    line {e.line}: {e.message}
                  </span>
                ))}
                {warnings.map((e, i) => (
                  <span key={`w${i}`} className="jp-error jp-error--warning">
                    line {e.line}: {e.message}
                  </span>
                ))}
              </div>
            )}

            {savedList.length > 0 && (
              <div className="jp-panel" style={{ marginTop: "1rem" }}>
                <div className="jp-label" style={{ marginBottom: "0.5rem" }}>
                  saved on this device
                </div>
                <div className="jp-saved-list">
                  {savedList.map((s) => (
                    <div key={s.id} className="jp-saved-row">
                      <span>
                        {s.title}
                        {s.id === savedId ? " · editing" : ""}
                      </span>
                      <span style={{ display: "flex", gap: "0.7rem" }}>
                        <button type="button" onClick={() => loadSaved(s.id)}>
                          load
                        </button>
                        <button type="button" onClick={() => removeSaved(s.id)}>
                          delete
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <JianpuSheet song={song} activeIndex={player.activeIndex} />
            {song.notes.length === 0 && (
              <p
                className="jp-no-print"
                style={{ marginTop: "0.8rem", fontSize: "0.85rem", color: "var(--jp-ink-dim)" }}
              >
                Notes appear here as you type.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JianpuCompose;
